import {
  createBranch as publicCreateBranch,
  getBranch as publicGetBranch,
  updateBranch as publicUpdateBranch,
  deleteBranch as publicDeleteBranch,
  queryBranches as publicQueryBranches,
  typedQueryBranches as publicTypedQueryBranches,
  setDefaultBranch as publicSetDefaultBranch,
  getDefaultBranch as publicGetDefaultBranch,
  bulkUpdateBranchTags as publicBulkUpdateBranchTags,
  bulkUpdateBranchTagsByFilter as publicBulkUpdateBranchTagsByFilter,
} from './editor-branches-v1-branch-branches.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  BranchQuery,
  BranchesQueryBuilder,
  typedQueryBranches as universalTypedQueryBranches,
} from './editor-branches-v1-branch-branches.universal.js';
import { onBranchCreated as publicOnBranchCreated } from './editor-branches-v1-branch-branches.public.js';
import { onBranchDeleted as publicOnBranchDeleted } from './editor-branches-v1-branch-branches.public.js';
import { onBranchUpdated as publicOnBranchUpdated } from './editor-branches-v1-branch-branches.public.js';

function customQueryBranches(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: () => publicQueryBranches(httpClient)(),
    typedQueryFunction: (query: BranchQuery) =>
      publicTypedQueryBranches(httpClient)(query),
    hasOptionsParameter: false,
  });

  function overloadedQuery(): BranchesQueryBuilder;
  function overloadedQuery(
    query: BranchQuery
  ): ReturnType<typeof universalTypedQueryBranches>;
  function overloadedQuery(query?: BranchQuery): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createBranch: MaybeContext<
  BuildRESTFunction<typeof publicCreateBranch> & typeof publicCreateBranch
> = /*#__PURE__*/ createRESTModule(publicCreateBranch);
export const getBranch: MaybeContext<
  BuildRESTFunction<typeof publicGetBranch> & typeof publicGetBranch
> = /*#__PURE__*/ createRESTModule(publicGetBranch);
export const updateBranch: MaybeContext<
  BuildRESTFunction<typeof publicUpdateBranch> & typeof publicUpdateBranch
> = /*#__PURE__*/ createRESTModule(publicUpdateBranch);
export const deleteBranch: MaybeContext<
  BuildRESTFunction<typeof publicDeleteBranch> & typeof publicDeleteBranch
> = /*#__PURE__*/ createRESTModule(publicDeleteBranch);
export const setDefaultBranch: MaybeContext<
  BuildRESTFunction<typeof publicSetDefaultBranch> &
    typeof publicSetDefaultBranch
> = /*#__PURE__*/ createRESTModule(publicSetDefaultBranch);
export const getDefaultBranch: MaybeContext<
  BuildRESTFunction<typeof publicGetDefaultBranch> &
    typeof publicGetDefaultBranch
> = /*#__PURE__*/ createRESTModule(publicGetDefaultBranch);
export const bulkUpdateBranchTags: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateBranchTags> &
    typeof publicBulkUpdateBranchTags
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateBranchTags);
export const bulkUpdateBranchTagsByFilter: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateBranchTagsByFilter> &
    typeof publicBulkUpdateBranchTagsByFilter
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateBranchTagsByFilter);
export const queryBranches: MaybeContext<
  BuildRESTFunction<typeof customQueryBranches> & typeof customQueryBranches
> = /*#__PURE__*/ createRESTModule(customQueryBranches);
/**
 * Triggered when a branch is created.
 */
export const onBranchCreated: BuildEventDefinition<
  typeof publicOnBranchCreated
> &
  typeof publicOnBranchCreated = createEventModule(publicOnBranchCreated);
/**
 * Triggered when a branch is deleted.
 */
export const onBranchDeleted: BuildEventDefinition<
  typeof publicOnBranchDeleted
> &
  typeof publicOnBranchDeleted = createEventModule(publicOnBranchDeleted);
/**
 * Triggered when a branch is updated.
 */
export const onBranchUpdated: BuildEventDefinition<
  typeof publicOnBranchUpdated
> &
  typeof publicOnBranchUpdated = createEventModule(publicOnBranchUpdated);

export {
  Type,
  SourceType,
  EditorTypeOptions,
  SortOrder,
  State,
  SiteCreatedContext,
  Namespace,
  DeleteStatus,
  WebhookIdentityType,
} from './editor-branches-v1-branch-branches.universal.js';
export {
  Branch,
  BranchSourceOneOf,
  EditorType,
  SourceBranch,
  SourceTemplate,
  ExtendedFields,
  PublicTags,
  TagList,
  TagsModified,
  CreateBranchRequest,
  CreateBranchResponse,
  GetBranchRequest,
  GetBranchResponse,
  UpdateBranchRequest,
  UpdateBranchResponse,
  DeleteBranchRequest,
  DeleteBranchResponse,
  QueryBranchesRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Sorting,
  CursorPaging,
  QueryBranchesResponse,
  CursorPagingMetadata,
  Cursors,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  HtmlSitePublished,
  Page,
  Empty,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Asset,
  SiteCreated,
  SiteTransferred,
  SiteDeleted,
  DeleteContext,
  SiteUndeleted,
  SitePublished,
  SiteUnpublished,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  ServiceProvisioned,
  ServiceRemoved,
  SiteRenamed,
  SiteHardDeleted,
  NamespaceChanged,
  StudioAssigned,
  StudioUnassigned,
  SiteUrlChanged,
  SitePurgedExternally,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  WixelAssigned,
  WixelUnassigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  UserDomainMediaEnabled,
  UserDomainMediaDisabled,
  EditorlessAssigned,
  EditorlessUnassigned,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  SetDefaultBranchRequest,
  SetDefaultBranchResponse,
  GetDefaultBranchRequest,
  GetDefaultBranchResponse,
  BulkUpdateBranchTagsRequest,
  BulkUpdateBranchTagsResponse,
  ItemMetadata,
  ApplicationError,
  BulkUpdateBranchTagsResult,
  BulkActionMetadata,
  BulkUpdateBranchTagsByFilterRequest,
  BulkUpdateBranchTagsByFilterResponse,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  BranchCreatedEnvelope,
  BranchDeletedEnvelope,
  BranchUpdatedEnvelope,
  UpdateBranch,
  BranchesQueryResult,
  BranchesQueryBuilder,
  BranchQuerySpec,
  BulkUpdateBranchTagsOptions,
  BulkUpdateBranchTagsByFilterOptions,
} from './editor-branches-v1-branch-branches.universal.js';
export { utils } from './editor-branches-v1-branch-branches.universal.js';
export {
  TypeWithLiterals,
  SourceTypeWithLiterals,
  EditorTypeOptionsWithLiterals,
  SortOrderWithLiterals,
  StateWithLiterals,
  SiteCreatedContextWithLiterals,
  NamespaceWithLiterals,
  DeleteStatusWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CreateBranchApplicationErrors,
  UpdateBranchApplicationErrors,
  DeleteBranchApplicationErrors,
  BulkUpdateBranchTagsApplicationErrors,
  BulkUpdateBranchTagsByFilterApplicationErrors,
  CommonQueryWithEntityContext,
  BranchQuery,
} from './editor-branches-v1-branch-branches.universal.js';
